(() => {
  if (window.CzCart?.initialized) return;

  const root = window.Shopify?.routes?.root || '/';
  let toastTimer = null;
  let drawerObserver = null;
  let mobileBarObserver = null;
  let drawerRefreshVersion = 0;
  let addQueue = Promise.resolve();

  // Serialize existing add requests so two cards cannot render conflicting totals.
  const enqueueAdd = (action) => {
    const result = addQueue.then(action);
    addQueue = result.catch(() => {});
    return result;
  };

  const cartUrl = (path) => `${root}${path}`.replace(/\/\/{2,}/g, '/');

  const updateCartBadges = (count) => {
    const normalizedCount = Math.max(0, Number.parseInt(count, 10) || 0);
    document.querySelectorAll('[data-cz-cart-count]').forEach((badge) => {
      badge.textContent = normalizedCount;
      badge.hidden = normalizedCount === 0;
      const cartLink = badge.closest('[data-cz-cart-label]');
      if (cartLink) cartLink.setAttribute('aria-label', `${cartLink.dataset.czCartLabel}: ${normalizedCount}`);
    });
  };

  const setQuickAddLabel = (button, label) => {
    const labelNode = button?.querySelector('[data-cz-quick-add-label]');
    if (labelNode) labelNode.textContent = label;
    else if (button) button.textContent = label;
  };

  const syncProductCardCartState = (cart) => {
    const productQuantities = new Map();
    const variantQuantities = new Map();

    (cart?.items || []).forEach((item) => {
      const quantity = Math.max(0, Number.parseInt(item.quantity, 10) || 0);
      const productId = String(item.product_id || '');
      const variantId = String(item.variant_id || item.id || '');
      if (productId) productQuantities.set(productId, (productQuantities.get(productId) || 0) + quantity);
      if (variantId) variantQuantities.set(variantId, (variantQuantities.get(variantId) || 0) + quantity);
    });

    document.querySelectorAll('[data-cz-product-card]').forEach((card) => {
      const productId = card.dataset.czProductId || '';
      const variantId = card.dataset.czVariantId || '';
      const quantity = productQuantities.get(productId) || variantQuantities.get(variantId) || 0;
      const inCart = quantity > 0;
      const button = card.querySelector('[data-cz-quick-add]');

      card.classList.toggle('is-in-cart', inCart);
      card.dataset.czCartQuantity = String(quantity);
      if (!button) return;

      if (!button.dataset.czAddAriaLabel) button.dataset.czAddAriaLabel = button.getAttribute('aria-label') || '';
      button.classList.toggle('is-added', inCart);
      button.dataset.czInCart = String(inCart);
      button.setAttribute('aria-label', inCart
        ? `${card.dataset.czProductTitle || 'Este producto'} ya está en el carro. Agregar otra unidad.`
        : button.dataset.czAddAriaLabel);

      if (button.dataset.czBusy !== 'true') {
        setQuickAddLabel(button, inCart
          ? (button.dataset.czAddedLabel || 'Agregado')
          : (button.dataset.czAddLabel || 'Agregar'));
      }
    });
  };

  const refreshProductCardCartState = async () => {
    if (!document.querySelector('[data-cz-product-card]')) return null;
    try {
      const response = await fetch(cartUrl('cart.js'), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });
      const cart = await parseResponse(response);
      updateCartBadges(cart.item_count || 0);
      syncProductCardCartState(cart);
      return cart;
    } catch (error) {
      return null;
    }
  };

  const getCartDrawer = () => document.getElementById('cart-drawer');
  const getCartDrawerSection = () => document.getElementById('shopify-section-cart-drawer-section');

  const captureDrawerIntent = (trigger = document.activeElement) => {
    const drawer = getCartDrawer();
    return { drawer, dismissalVersion: drawer?.dismissalVersion || 0, trigger };
  };

  const isCurrentIntent = (intent) => !intent || (
    intent.drawer === getCartDrawer() && intent.dismissalVersion === (intent.drawer?.dismissalVersion || 0)
  );

  const closeCartDrawer = (event) => {
    const drawer = event.currentTarget.closest('#cart-drawer');
    if (typeof drawer?.close !== 'function') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    drawer.close();
  };

  const bindCartDrawerCloseButtons = () => {
    const drawer = getCartDrawer();
    if (!drawer) return;

    drawer.querySelectorAll('.theme-drawer__close-button, [data-cz-cart-close]').forEach((button) => {
      if (button.dataset.czCartCloseBound === 'true') return;
      button.dataset.czCartCloseBound = 'true';
      button.addEventListener('click', closeCartDrawer, true);
    });
  };

  const syncCartBadgeFromDrawer = () => {
    const drawer = getCartDrawer();
    if (!drawer) return;
    if (drawer.querySelector('.cart-drawer--empty')) {
      updateCartBadges(0);
      return;
    }
    const badge = drawer.querySelector('.theme-drawer__badge');
    const count = badge ? Number.parseInt(badge.textContent, 10) : Number.NaN;
    if (Number.isFinite(count)) updateCartBadges(count);
  };

  const observeCartDrawer = () => {
    drawerObserver?.disconnect();
    const section = getCartDrawerSection();
    if (!section || !window.MutationObserver) return;
    let cardSyncTimer;
    drawerObserver = new MutationObserver(() => {
      bindCartDrawerCloseButtons();
      syncCartBadgeFromDrawer();
      clearTimeout(cardSyncTimer);
      cardSyncTimer = setTimeout(refreshProductCardCartState, 150);
    });
    drawerObserver.observe(section, { childList: true, subtree: true, characterData: true });
    bindCartDrawerCloseButtons();
    syncCartBadgeFromDrawer();
  };

  const refreshCartDrawer = async () => {
    const currentSection = getCartDrawerSection();
    if (!currentSection) return null;
    const refreshVersion = ++drawerRefreshVersion;

    const sectionUrl = new URL(window.location.href);
    sectionUrl.hash = '';
    sectionUrl.searchParams.set('section_id', 'cart-drawer-section');

    const response = await fetch(sectionUrl.toString(), {
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) throw new Error('No pudimos actualizar el carro.');

    const markup = await response.text();
    const documentFragment = new DOMParser().parseFromString(markup, 'text/html');

    if (refreshVersion !== drawerRefreshVersion) return null;
    const drawer = getCartDrawer();
    const currentContent = drawer?.querySelector('cart-drawer-component');
    const nextContent = documentFragment.querySelector('#cart-drawer cart-drawer-component');
    if (!currentContent || !nextContent) throw new Error('No pudimos mostrar el carro.');

    // Keep the dialog owner, dismissal history and focus origin through a refresh.
    const hadFocus = drawer.contains(document.activeElement);
    currentContent.replaceWith(document.importNode(nextContent, true));
    if (drawer.isOpen && hadFocus) drawer.refreshFocusTrap?.();

    observeCartDrawer();
    await customElements.whenDefined('theme-drawer');
    return getCartDrawer();
  };

  const openCartDrawer = async ({ refresh = false, intent } = {}) => {
    let drawer = getCartDrawer();
    if (!drawer || !window.customElements) return false;

    if (refresh) drawer = await refreshCartDrawer();
    if (!drawer) return false;

    await customElements.whenDefined('theme-drawer');
    if (typeof drawer.open !== 'function') return false;
    // Updating the contents is allowed after dismissal; reopening is not.
    if (!isCurrentIntent(intent)) return true;
    if (!drawer.isOpen) drawer.open({ trigger: intent?.trigger });
    return true;
  };

  const ensureToast = () => {
    let toast = document.querySelector('[data-cz-cart-toast]');
    if (toast) return toast;
    toast = document.createElement('aside');
    toast.className = 'cz-cart-toast';
    toast.dataset.czCartToast = '';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = '<div class="cz-cart-toast__top"><div><span class="cz-cart-toast__label" data-cz-toast-label></span><strong class="cz-cart-toast__title" data-cz-toast-title></strong></div><button class="cz-cart-toast__close" type="button" data-cz-toast-close aria-label="Cerrar">×</button></div><div class="cz-cart-toast__actions"><button type="button" data-cz-toast-close>Seguir comprando</button><a href="' + cartUrl('cart') + '">Ver carro</a></div>';
    document.body.appendChild(toast);
    return toast;
  };

  const hideToast = () => {
    const toast = document.querySelector('[data-cz-cart-toast]');
    if (!toast) return;
    toast.classList.remove('is-visible');
    window.clearTimeout(toastTimer);
  };

  const showToast = (title, error = false) => {
    const toast = ensureToast();
    const label = toast.querySelector('[data-cz-toast-label]');
    const titleNode = toast.querySelector('[data-cz-toast-title]');
    if (label) label.textContent = error ? 'No pudimos actualizar el carro' : 'Agregado al carro';
    if (titleNode) titleNode.textContent = title || (error ? 'Inténtalo nuevamente.' : 'Producto agregado correctamente.');
    toast.classList.toggle('is-error', error);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(hideToast, error ? 6500 : 4800);
  };

  const parseResponse = async (response) => {
    let data = {};
    try { data = await response.json(); } catch (error) { data = {}; }
    if (!response.ok) throw new Error(data.description || data.message || 'No pudimos completar la acción.');
    return data;
  };

  const signalCartAddition = (response, requestedItems) => {
    document.dispatchEvent(new CustomEvent('cz:cart:added', {
      detail: { items: Array.isArray(response.items) ? response.items : [response], requestedItems }
    }));
  };

  const syncAfterAdd = async (item, successTitle, intent) => {
    let cart;
    try {
      const cartResponse = await fetch(cartUrl('cart.js'), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });
      cart = await parseResponse(cartResponse);
    } catch (error) {
      // The add was already accepted. A failed read must not invite another POST.
      document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { cart: null, item } }));
      showToast('Producto agregado. Abre el carro para ver el total actualizado.');
      return null;
    }
    updateCartBadges(cart.item_count || 0);
    syncProductCardCartState(cart);
    document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { cart, item } }));

    if (document.querySelector('[data-cz-cart-page]')) {
      window.setTimeout(() => window.location.reload(), 350);
    } else {
      let opened = false;
      try { opened = await openCartDrawer({ refresh: true, intent }); } catch (drawerError) { opened = false; }
      if (!opened) showToast(successTitle || item.product_title || item.title || 'Producto agregado correctamente.');
    }

    return cart;
  };

  const addItemsToCart = async (items, { successTitle } = {}) => {
    const intent = captureDrawerIntent();
    const normalizedItems = Array.isArray(items)
      ? items
        .map((item) => ({
          id: Number.parseInt(item.id, 10),
          quantity: Math.max(1, Number.parseInt(item.quantity, 10) || 1)
        }))
        .filter((item) => Number.isFinite(item.id))
      : [];

    if (normalizedItems.length === 0) throw new Error('Selecciona al menos un producto.');

    try {
      return await enqueueAdd(async () => {
        const billing = document.querySelector('cz-retail-billing');
        if (billing) await billing.persist();
        const response = await fetch(cartUrl('cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: normalizedItems })
        });
        const item = await parseResponse(response);
        signalCartAddition(item, normalizedItems);
        const cart = await syncAfterAdd(item, successTitle || 'Productos agregados correctamente.', intent);
        return { item, cart };
      });
    } catch (error) {
      showToast(error.message, true);
      throw error;
    }
  };

  const setAddButtonsBusy = (form, busy) => {
    const scope = form.closest('[data-cz-product-card], [data-cz-product-root]') || form;
    const buttons = Array.from(scope.querySelectorAll('.cz-quick-add, [data-cz-add-button]'));
    buttons.forEach((button) => {
      const quickAdd = button.matches('[data-cz-quick-add]');
      if (busy) {
        if (!quickAdd && !button.dataset.czOriginalText) button.dataset.czOriginalText = button.textContent;
        button.dataset.czBusy = 'true';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (quickAdd) setQuickAddLabel(button, '…');
        else button.textContent = 'Agregando…';
      } else {
        button.dataset.czBusy = 'false';
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (quickAdd) {
          setQuickAddLabel(button, button.classList.contains('is-added')
            ? (button.dataset.czAddedLabel || 'Agregado')
            : (button.dataset.czAddLabel || 'Agregar'));
        } else if (button.dataset.czOriginalText) {
          button.textContent = button.dataset.czOriginalText;
        }
      }
    });
  };

  const addToCart = async (form, trigger) => {
    if (form.dataset.czPending === 'true') return;
    const intent = captureDrawerIntent(trigger || form.querySelector('[type="submit"]'));
    form.dataset.czPending = 'true';
    setAddButtonsBusy(form, true);
    try {
      await enqueueAdd(async () => {
        const billing = document.querySelector('cz-retail-billing');
        if (billing) await billing.persist();
        const formData = new FormData(form);
        const response = await fetch(cartUrl('cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          body: formData
        });
        const item = await parseResponse(response);
        signalCartAddition(item, [{ id: formData.get('id'), quantity: formData.get('quantity') || 1 }]);
        await syncAfterAdd(item, undefined, intent);
      });
    } catch (error) {
      showToast(error.message, true);
    } finally {
      form.dataset.czPending = 'false';
      setAddButtonsBusy(form, false);
    }
  };

  const changeLine = async (row, quantity) => {
    if (!row || row.dataset.czPending === 'true') return;
    const key = row.dataset.czLineKey;
    if (!key) return;
    const input = row.querySelector('[data-cz-cart-quantity]');
    const confirmedQuantity = input?.defaultValue;
    const controls = Array.from(row.querySelectorAll('button, input')).filter((control) => !control.disabled);
    row.dataset.czPending = 'true';
    row.setAttribute('aria-busy', 'true');
    controls.forEach((control) => { control.disabled = true; });
    row.classList.add('is-pending');
    try {
      const billing = document.querySelector('cz-retail-billing');
      if (billing) await billing.persist();
      const response = await fetch(cartUrl('cart/change.js'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: Math.max(0, Number.parseInt(quantity, 10) || 0) })
      });
      const cart = await parseResponse(response);
      updateCartBadges(cart.item_count || 0);
      window.location.reload();
    } catch (error) {
      if (input && confirmedQuantity !== undefined) input.value = confirmedQuantity;
      row.dataset.czPending = 'false';
      row.removeAttribute('aria-busy');
      controls.forEach((control) => { control.disabled = false; });
      row.classList.remove('is-pending');
      showToast(error.message, true);
    }
  };

  const setupMobileCheckoutBar = () => {
    mobileBarObserver?.disconnect();
    const bar = document.querySelector('[data-cz-cart-mobile-bar]');
    const summary = document.querySelector('[data-cz-cart-summary]');
    if (!bar || !summary || !window.IntersectionObserver) return;

    mobileBarObserver = new IntersectionObserver((entries) => {
      const summaryVisible = entries.some((entry) => entry.isIntersecting);
      bar.classList.toggle('is-hidden', summaryVisible);
      bar.toggleAttribute('inert', summaryVisible);
      bar.setAttribute('aria-hidden', summaryVisible ? 'true' : 'false');
    }, { threshold: 0.18 });
    mobileBarObserver.observe(summary);
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.cz-product-form, .cz-quick-add-form');
    if (!form || !window.fetch) return;
    event.preventDefault();
    addToCart(form, event.submitter);
  });

  document.addEventListener('click', async (event) => {
    if (event.target.closest('[data-cz-toast-close]')) {
      hideToast();
      return;
    }

    const cartTrigger = event.target.closest('[data-cz-cart-open]');
    if (cartTrigger) {
      const drawer = getCartDrawer();
      if (!drawer) return;
      event.preventDefault();
      try {
        const opened = await openCartDrawer({ intent: captureDrawerIntent(cartTrigger) });
        if (!opened) window.location.assign(cartTrigger.href);
      } catch (error) {
        window.location.assign(cartTrigger.href);
      }
      return;
    }

    const remove = event.target.closest('[data-cz-cart-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(remove.closest('[data-cz-cart-line]'), 0);
      return;
    }

    const minus = event.target.closest('[data-cz-cart-minus]');
    const plus = event.target.closest('[data-cz-cart-plus]');
    if (minus || plus) {
      const row = (minus || plus).closest('[data-cz-cart-line]');
      const input = row?.querySelector('[data-cz-cart-quantity]');
      if (!row || !input || row.dataset.czPending === 'true') return;
      const minimum = Number(input.min) || 1;
      const increment = Number(input.step) || 1;
      const maximum = input.max ? Number(input.max) : Infinity;
      const current = Number(input.value) || minimum;
      const next = minus ? Math.max(minimum, current - increment) : current + increment;
      if (next === current || next > maximum) return;
      input.value = next;
      if (!input.checkValidity()) { input.reportValidity(); return; }
      changeLine(row, next);
    }
  });

  document.addEventListener('click', (event) => {
    const review = event.target.closest('[data-cz-billing-review]');
    if (!review || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(review.href);
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cz-cart-quantity]');
    if (!input) return;
    const row = input.closest('[data-cz-cart-line]');
    if (!row || row.dataset.czPending === 'true') return;
    if (input.value.trim() === '' || !input.checkValidity() || !Number.isSafeInteger(Number(input.value))) { input.reportValidity(); return; }
    changeLine(row, Number(input.value));
  });

  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target?.id !== 'cart-drawer') return;
    document.querySelectorAll('[data-cz-cart-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    syncCartBadgeFromDrawer();
  });

  document.addEventListener('theme-drawer:close', (event) => {
    if (event.target?.id !== 'cart-drawer') return;
    document.querySelectorAll('[data-cz-cart-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  });

  document.addEventListener('cz:cart:updated', (event) => {
    if (event.detail?.cart) syncProductCardCartState(event.detail.cart);
    else refreshProductCardCartState();
  });

  document.addEventListener('shopify:section:load', () => refreshProductCardCartState());
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshProductCardCartState();
  });

  window.CzCart = {
    ...(window.CzCart || {}),
    initialized: true,
    addItems: addItemsToCart,
    refreshProductCards: refreshProductCardCartState
  };

  observeCartDrawer();
  setupMobileCheckoutBar();
  refreshProductCardCartState();
})();

